import debug from 'debug';
import validate from 'validate.js';

const log = debug('ValidateForm:Engine');

class Engine {
	/**
	 * @param {element} formElement 
	 * @param {Array} formFields 
	 */
	constructor(formElement, formFields) {
		this.formElement = formElement;
		this.formFields = formFields;

		this.initialize();
	}

	/**
	 * @param {element} formField
	 * @returns {Promise} [messages (success/failed), key]
	 */
	 checkField(formField) {
		const key = formField.getAttribute('data-validate');
		const attributes = this.getAttributes(formField, key);

		return new Promise((resolve, reject) => {
			validate.async(attributes, this.constraints).then((success) => {
				resolve({
					messages: success,
					key: key
				});
			}, (failed) => {
				resolve({
					messages: failed,
					key: key
				});
			});
		});
	}

	/**
	 * @param {element} formField
	 * @param {string} key
	 * @returns {object}
	 */
	getAttributes(formField, key) {
		let attributes = JSON.parse(`{ "${ key }": "${ formField.value }" }`);

		if (formField.type === 'checkbox') {
			attributes = JSON.parse(`{ "${ key }": "${ formField.checked }" }`);
		}

		if (formField.getAttribute('data-validate-equality') !== null) {
			const equalityKey = formField.getAttribute('data-validate-equality');
			const equalityField = this.formElement.querySelector(`[data-validate='${ equalityKey }']`)
			attributes = JSON.parse(`{ "${ key }": "${ formField.value }", "${ equalityKey }": "${ equalityField.value }" }`);
		}

		return attributes;
	}

	/**
	 * @returns {object}
	 */
	getConstraints() {
		const constraints = [];
		this.formFields.map(field => {
			let keyObj = {};

			const optionalEmailMessage = field.getAttribute('data-validate-optional-email'); 
			if(optionalEmailMessage !== null) {
								
				let emailObj = {
					message: '^' + optionalEmailMessage
				};
				
				keyObj.optionalEmail = emailObj;
			}

			const requiredMessage = field.getAttribute('data-validate-required');
			if(requiredMessage !== null) {
				log('required', field.id, requiredMessage);
				let requiredObj = {};
				
				requiredObj['allowEmpty'] = false;
				requiredObj['message'] = '^' + requiredMessage;
				
				keyObj['presence'] = requiredObj;
			}

			const checkedMessage = field.getAttribute('data-validate-checked');
			if(checkedMessage !== null) {
				log('checkbox', field.id);
				let checkedObj = {
					message: '^' + checkedMessage
				};
			
				keyObj.checked = checkedObj;
			}

			const validateEmailDomain = field.getAttribute('data-validate-email-domain') === 'true';
			const emailDomainMessage = field.getAttribute('data-validate-email-domain-message');
			const emailDomainValidationUrl = field.getAttribute('data-email-domain-validation-url');
			const emailDomainValidationApiKey = field.getAttribute('data-email-domain-validation-apikey');

			if(validateEmailDomain && emailDomainMessage !== null) {
				log('email', field.id);
				let emailObj = {
					message: '^' + emailDomainMessage,
					url: emailDomainValidationUrl,
					apiKey: emailDomainValidationApiKey
				};
				
				keyObj['emailDomain'] = emailObj;
			}

			const emailMessage = field.getAttribute('data-validate-email');
			if(emailMessage !== null) {
				
				log('email', field.id);	
				let emailObj = {
					message: '^' + emailMessage
				};
				
				keyObj.email = emailObj;
			}

			const equalityField = field.getAttribute('data-validate-equality');
			const equalityMessage = field.getAttribute('data-validate-equality-message');
			if(equalityField !== null && equalityMessage !== null) {
				log('equality', field.id);
				let equalityObj = {
					attribute: equalityField,
					message: '^' + equalityMessage
				};
				
				keyObj['equality'] = equalityObj;
			}

			const excludeWords = field.getAttribute('data-validate-exclude');
			const excludeMessage = field.getAttribute('data-validate-exclude-message');
			if(excludeWords !== null && excludeMessage !== null) {
				log('exclude words', field.id);
				let excludeObj = {
					words: excludeWords.replace(' ', ''),
					message: '^' + excludeMessage
				};
				
				keyObj['exclude'] = excludeObj;
			}

			const pattern = field.getAttribute('data-validate-pattern');
			const patternMessage = field.getAttribute('data-validate-pattern-message');
			if(pattern !== null && patternMessage !== null) {
				log('pattern', field.id);
				let patternObj = {
					pattern: pattern,
					message: '^' + patternMessage
				};
				
				keyObj['format'] = patternObj;
			}

			const key = field.getAttribute('data-validate');
			constraints[key] = keyObj;
		});

		return constraints;
	}

	initialize() {
		this.constraints = this.getConstraints();
		this.setCustomValidators();
	}

	setCustomValidators() {
		validate.validators.checked = function (value, options, key, attributes) {
			if (value !== 'true') return options.message || 'must be checked';
		};

		/**
		 * Validate as email, but allow empty values
		 * @see https://github.com/ansman/validate.js/issues/182
		 * @see https://validatejs.org/#custom-validator
		 */
		validate.validators.optionalEmail = (value, options) => {

			if (value === '') {
				return null;
			}

			let constraints = {
				optionalEmail: {
				  email: {
					message: '^' + options.message
				  }
				}
			  };
			  
			let notValid = validate({optionalEmail: value}, constraints);

			return notValid ? notValid.optionalEmail : null;

		};
 
		/**
		 * Run check against a service to determine if the email domain is valid
		 */
		validate.validators.emailDomain = function (value, options, key, attributes) {

			return new Promise ((resolve, reject) => {

				// No need to move on if we have no value, or url and key for the service
				if (!value || !(options.url && options.apiKey) ) {
					resolve();
					return;
				}

				let domain = value.split('@')[1];

				if (!domain || options.url == '') {
					resolve();
					return;
				}

				fetch(`//${options.url}/CheckMxRecord?domain=${domain}`, {
					method: 'GET',
					headers: {
						'Accept': 'application/json',
						'Ocp-Apim-Subscription-Key': options.apiKey
					},
					credentials: 'include'
				}).then(response => {
					return response.json();
				}).then(data => {
					if (data.Success) {
						resolve();
					}
					else {
						resolve(options.message);
					}
				}).catch(response => {
					log(`validators:emailDomain request failed with: ${response}`);
					resolve();
				});
			});
		}

		validate.validators.exclude = function (value, options, key, attributes) {
			var words = options.words.split(',');
			var containsWord = false;
			if (value != null) {
				for (var i = 0; i < words.length; ++i) {
					if (value.toLowerCase().indexOf(words[i].toLowerCase()) > -1) {
						containsWord = true;
					}
				}
				if (containsWord) {
					return options.message || 'not valid words';
				}
			}
		};
	}
}

export default Engine;
